import React, { useCallback, useRef, useState } from 'react'

import MobileSelectDates from '../Common/MobileSelectDates'

import { showSnackbar } from 'components/Luxkit/Snackbar/AppSnackbar'
import noop from 'lib/function/noop'
import { useTrip, useUpdateTrip } from 'tripPlanner/hooks/api'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import * as Mapper from 'tripPlanner/mappers'

interface Props {
  tripId: string
  onDatesAdded?: (startDate: moment.Moment, endDate: moment.Moment) => void
}

/** Just like the version in EditTripDatesMC, except this one is for use with the Trip Planner useOpenModal() hook */
function EditTripDates(props: Props) {
  const { tripId, onDatesAdded = noop } = props

  const { data: trip } = useTrip({ tripId })

  const { startDate, endDate } = trip ?? {}

  const initialHasDates = useRef(!!(startDate && endDate)).current

  const { closeModal } = useModalCallbacks()

  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined)

  const { mutate, isLoading } = useUpdateTrip({
    onMutate: (): undefined => {
      setErrorMsg(undefined)
    },
    onSuccess: (updatedTripRaw) => {
      const trip = Mapper.fullTrip(updatedTripRaw)
      closeModal()
      const hasDates = !!(trip.startDate && trip.endDate)
      if (initialHasDates && !hasDates) {
        showSnackbar('Dates removed', 'success')
      } else if (hasDates) {
        showSnackbar('Dates saved', 'success')
        onDatesAdded(trip.startDate!, trip.endDate!)
      }
    },
    onError: () => {
      setErrorMsg('Error while updating trip dates. Please try again.')
    },
  })

  const updateTripDates = useCallback(
    (startDate: moment.Moment, endDate?: moment.Moment) => {
      mutate({
        tripId,
        startDate,
        endDate: endDate ?? startDate,
      })
    },
    [mutate, tripId],
  )

  const clearTripDates = useCallback(() => {
    mutate({
      tripId,
      startDate: null,
      endDate: null,
    })
  }, [mutate, tripId])

  return (
    <MobileSelectDates
      initialStartDate={startDate}
      initialEndDate={endDate}
      onChange={updateTripDates}
      onRemove={startDate ? clearTripDates : undefined}
      onCancel={closeModal}
      title={startDate ? 'Edit dates' : 'Add dates'}
      doneLabel={startDate ? 'Save' : 'Add dates'}
      isSubmitting={isLoading}
      errorMsg={errorMsg}
    />
  )
}

export default EditTripDates
